import { Extension, Node, getSplittedAttributes } from '@tiptap/core';
import { closeHistory } from '@tiptap/pm/history';
import { AllSelection, NodeSelection, Plugin, PluginKey, Selection, TextSelection } from '@tiptap/pm/state';

/**
 * Manual page break (`pageBreak`).
 *
 * - An atom, non-editable block. Saved HTML is exactly
 *   `<div data-page-break="true" class="page-break"></div>`; the exporters and the Pages view
 *   (src/extensions/Pagination.js) end the page there.
 * - Parses `div[data-page-break]` plus legacy print breaks: an empty `div` or `p` whose inline
 *   style says `page-break-after/before: always` or `break-after/before: page`, and the `hr`
 *   (Google Docs) or `br` (Word) with such a style that a paste from those apps carries.
 * - Lives only at the top level of the document. `insertPageBreak()` places it there (see
 *   `resolvePageBreakPlacement`), and a small plugin moves any break that ends up nested (paste,
 *   drop, wrap in a list or quote) to just after its top-level ancestor block. Generated field
 *   wrappers and trust sections are transparent: they are split around the break instead.
 * - `insertPageBreak()` replaces a text or Select all selection, never leaves the next block
 *   node-selected, and is its own undo step.
 * - Ctrl+Enter / Cmd+Enter inserts one everywhere except inside a code block, where TipTap's
 *   exitCode keeps the key. The shortcut lives in a child extension with a priority above
 *   HardBreak (100) so it wins over HardBreak's own Mod-Enter, without moving the node itself
 *   ahead of `paragraph` in the schema. Shift+Enter stays a line break.
 * - In the editor a node view adds `role="separator"` and a "Page break" label; the continuous
 *   views style it in src/components/editor/editorContentStyles.js (`.page-break`). The node
 *   view also mirrors `data-block-id` and the review attributes (`data-diff-block`,
 *   `data-change-id`) so Inky reviews highlight breaks; saved HTML never carries them.
 */

export const PAGE_BREAK_NODE = 'pageBreak';
export const PAGE_BREAK_HTML = '<div data-page-break="true" class="page-break"></div>';
export const PAGE_BREAK_LABEL = 'Page break';

export const pageBreakPluginKey = new PluginKey('quillPageBreak');

/** Top-level text blocks that a break splits at the cursor (others get the break after them). */
const SPLITTABLE_TYPES = new Set(['paragraph', 'heading']);

/**
 * Wrapper blocks that hold whole stretches of a document (a generated field wrapper, which in a
 * free-form generated document wraps the entire body, and a trust section). Page breaks treat
 * them as transparent: the wrapper is split around the break (wrapper, break, wrapper), so a
 * break can still go mid-document. Both halves keep the wrapper's attributes (field id, review
 * state) except the block id.
 */
export const PAGE_BREAK_TRANSPARENT_TYPES = new Set(['generatedField', 'trustSection']);

/** Review states a moved break inherits from the block it was nested in. */
const REVIEW_KINDS = new Set(['insert', 'delete']);

/**
 * Whether an inline style asks for a print page break.
 * @param {string|null|undefined} style - Raw `style` attribute.
 * @returns {boolean} True for `page-break-after/before: always` or `break-after/before: page`.
 */
export function styleHasPageBreak(style) {
  const css = String(style || '').toLowerCase().replace(/\s+/g, '');
  return /(?:^|;)page-break-(?:after|before):always(?:!important)?(?:;|$)/.test(css)
    || /(?:^|;)break-(?:after|before):page(?:!important)?(?:;|$)/.test(css);
}

/**
 * Whether an element is a legacy print break with nothing else in it (no text, images,
 * tables or other content that a break node would silently drop).
 * @param {HTMLElement} element - Candidate element.
 * @returns {boolean} True when it can safely become a page break node.
 */
const isLegacyBreakElement = (element) => {
  if (!styleHasPageBreak(element.getAttribute('style'))) return false;
  if ((element.textContent || '').replace(/ /g, ' ').trim()) return false;
  return !element.querySelector('img, table, svg, video, iframe, hr, input, [data-tpl-field], [data-mermaid-block]');
};

/**
 * How many wrapper levels (see PAGE_BREAK_TRANSPARENT_TYPES) sit directly under the document at
 * a position: depth 1 is a wrapper, depth 2 a wrapper inside it, and so on.
 * @param {import('@tiptap/pm/model').ResolvedPos} $pos - Position.
 * @returns {number} Wrapper depth (0 when the position is not inside a wrapper).
 */
export const wrapperDepthAt = ($pos) => {
  let depth = 0;
  while (depth < $pos.depth && PAGE_BREAK_TRANSPARENT_TYPES.has($pos.node(depth + 1).type.name)) depth += 1;
  return depth;
};

/**
 * Where a page break goes for the current selection.
 * Wrappers (generated field, trust section) are transparent, so "top level" below means the
 * document or the innermost wrapper around the cursor. A node-selected top-level block gets the
 * break after it; inside a top-level paragraph or heading the block is split at the cursor (at
 * its start the break goes before it, at its end after it, so no empty fragment is left); inside
 * anything nested (list, table, quote) or any other top-level block (code block, diagram, ...)
 * the break goes after the top-level ancestor. A position inside a wrapper is then lifted to the
 * document's top level by splitting the wrapper there (see liftPageBreak).
 * @param {import('@tiptap/pm/state').Selection} selection - Selection (already collapsed for text).
 * @returns {{insertAt: number|null, splitAt: number|null, wrapperDepth: number}} Position for
 *   the break between blocks (at the top level or inside wrappers), or a text position to split
 *   first (the break then goes at `splitAt + 1`).
 */
export function resolvePageBreakPlacement(selection) {
  const $pos = selection.$from;
  const wrapperDepth = wrapperDepthAt($pos);
  if (selection instanceof NodeSelection && $pos.depth === wrapperDepth) {
    return { insertAt: selection.to, splitAt: null, wrapperDepth };
  }
  if ($pos.depth === wrapperDepth) return { insertAt: $pos.pos, splitAt: null, wrapperDepth };
  const blockDepth = wrapperDepth + 1;
  const parent = $pos.parent;
  if ($pos.depth === blockDepth && SPLITTABLE_TYPES.has(parent.type.name) && parent.isTextblock) {
    if ($pos.parentOffset === 0) return { insertAt: $pos.before(blockDepth), splitAt: null, wrapperDepth };
    if ($pos.parentOffset >= parent.content.size) return { insertAt: $pos.after(blockDepth), splitAt: null, wrapperDepth };
    return { insertAt: null, splitAt: $pos.pos, wrapperDepth };
  }
  return { insertAt: $pos.after(blockDepth), splitAt: null, wrapperDepth };
}

/**
 * Attributes for the second half of a split wrapper: everything except the block id (BlockId
 * gives it a fresh one), so the field id and any review state stay on both halves.
 * @param {object} attrs - Wrapper attributes.
 * @returns {object} Attributes.
 */
const splitWrapperAttrs = (attrs) => ('blockId' in attrs ? { ...attrs, blockId: null } : { ...attrs });

/**
 * Lift the page break node at `pos` out of the wrappers around it by splitting each wrapper
 * there. A break at a wrapper's start or end moves before or after it instead (no empty wrapper
 * is left), and a wrapper that holds only the break is replaced by it. Stops at the top level
 * or at a parent that is not a wrapper.
 * @param {import('@tiptap/pm/state').Transaction} tr - Transaction (mutated).
 * @param {number} pos - Position of the break node.
 * @returns {number} The break's final position.
 */
export function liftPageBreak(tr, pos) {
  let at = pos;
  for (let guard = 0; guard < 64; guard += 1) {
    const $at = tr.doc.resolve(at);
    const node = tr.doc.nodeAt(at);
    if (!$at.depth || !node || !PAGE_BREAK_TRANSPARENT_TYPES.has($at.parent.type.name)) break;
    const { depth, parent } = $at;
    const index = $at.index();
    const start = $at.before(depth);
    if (parent.childCount === 1) {
      tr.replaceWith(start, start + parent.nodeSize, node);
      at = start;
    } else if (index === 0) {
      tr.delete(at, at + node.nodeSize);
      tr.insert(start, node);
      at = start;
    } else if (index === parent.childCount - 1) {
      const end = $at.after(depth);
      tr.delete(at, at + node.nodeSize);
      at = end - node.nodeSize;
      tr.insert(at, node);
    } else {
      tr.split(at, 1, [{ type: parent.type, attrs: splitWrapperAttrs(parent.attrs) }]);
      at += 2; // now the first child of the second half; the next round moves it before that half
    }
  }
  return at;
}

/** Blocks a pasted Word page break splits. */
const PASTE_SPLIT_BLOCKS = 'p, h1, h2, h3, h4, h5, h6, li, div';
/** Content that keeps an otherwise empty split half. */
const PASTE_KEEP_CONTENT = 'img, table, svg, video, iframe, hr, input';

/**
 * Whether a split half has nothing worth keeping.
 * @param {Element} el - Element.
 * @returns {boolean} True when empty.
 */
const isEmptyHalf = (el) => !(el.textContent || '').replace(/ /g, ' ').trim() && !el.querySelector(PASTE_KEEP_CONTENT);

/**
 * Turn Word's clipboard page break (`<br clear=all style="mso-special-character:line-break;
 * page-break-before:always">`) into the canonical break element before the paste is parsed:
 * the paragraph (or heading, list item) around it is split there, with the break between the
 * two halves. Plain line breaks are left alone, and so is everything outside the body.
 * @param {string} html - Clipboard HTML.
 * @returns {string} HTML.
 */
export function normalizePastedPageBreaks(html) {
  const text = String(html || '');
  if (!/<br/i.test(text) || !/break-(?:before|after)/i.test(text) || typeof DOMParser === 'undefined') return text;
  const doc = new DOMParser().parseFromString(text, 'text/html');
  const breaks = Array.from(doc.body.querySelectorAll('br')).filter((br) => styleHasPageBreak(br.getAttribute('style')));
  if (!breaks.length) return text;
  breaks.forEach((br) => {
    const marker = doc.createElement('div');
    marker.setAttribute('data-page-break', 'true');
    marker.className = 'page-break';
    const block = br.parentElement && br.parentElement.closest(PASTE_SPLIT_BLOCKS);
    if (!block || !doc.body.contains(block)) {
      br.replaceWith(marker);
      return;
    }
    const range = doc.createRange();
    range.setStartAfter(br);
    range.setEndAfter(block.lastChild);
    const tail = range.extractContents();
    br.remove();
    const rest = block.cloneNode(false);
    rest.appendChild(tail);
    block.after(marker);
    marker.after(rest);
    if (isEmptyHalf(rest)) rest.remove();
    if (isEmptyHalf(block)) block.remove();
  });
  const body = doc.body.innerHTML;
  return /<body[\s>]/i.test(text) ? text.replace(/(<body[^>]*>)[\s\S]*(<\/body>)/i, (_m, open, close) => `${open}${body}${close}`) : body;
}

/**
 * Whether the selection sits inside a code block (where Mod-Enter keeps exitCode).
 * @param {import('@tiptap/pm/state').EditorState} state - Editor state.
 * @returns {boolean} True inside code.
 */
const selectionInCode = (state) => {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).type.spec.code) return true;
  }
  return false;
};

/**
 * Indexes of top-level children touched by the given transactions (in the final document).
 * @param {readonly import('@tiptap/pm/state').Transaction[]} transactions - Applied transactions.
 * @param {import('@tiptap/pm/model').Node} doc - Final document.
 * @returns {number[]} Sorted unique top-level child indexes.
 */
const touchedTopLevelIndexes = (transactions, doc) => {
  let ranges = [];
  transactions.forEach((tr) => {
    if (!tr.docChanged) return;
    ranges = ranges.map(([from, to]) => [tr.mapping.map(from, -1), tr.mapping.map(to, 1)]);
    tr.mapping.maps.forEach((map, index) => {
      const rest = tr.mapping.slice(index + 1);
      map.forEach((_oldStart, _oldEnd, newStart, newEnd) => {
        ranges.push([rest.map(newStart, -1), rest.map(newEnd, 1)]);
      });
    });
  });
  const size = doc.content.size;
  const indexes = new Set();
  ranges.forEach(([rawFrom, rawTo]) => {
    const from = Math.max(0, Math.min(rawFrom, rawTo, size));
    const to = Math.min(size, Math.max(rawFrom, rawTo, 0));
    const first = Math.max(0, doc.resolve(from).index(0) - 1);
    const last = Math.min(doc.childCount - 1, doc.resolve(to).index(0) + 1);
    for (let index = first; index <= last; index += 1) indexes.add(index);
  });
  return [...indexes].sort((a, b) => a - b);
};

/**
 * The attributes a break nested at `$pos` keeps once it is moved out: its own, plus the review
 * state of the nearest tagged ancestor when it has none (an Inky suggestion tags only the block
 * it inserts or deletes, so a break inside it must stay part of that change: rejecting the
 * suggestion then removes the break too).
 * @param {import('@tiptap/pm/model').ResolvedPos} $pos - Position of the nested break.
 * @param {import('@tiptap/pm/model').Node} node - The break.
 * @returns {object} Attributes for the moved break.
 */
const movedBreakAttrs = ($pos, node) => {
  if (!('diffBlock' in node.attrs) || REVIEW_KINDS.has(node.attrs.diffBlock)) return node.attrs;
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    const { attrs } = $pos.node(depth);
    if (REVIEW_KINDS.has(attrs.diffBlock)) {
      return { ...node.attrs, diffBlock: attrs.diffBlock, diffChangeId: attrs.diffChangeId || null };
    }
  }
  return node.attrs;
};

/**
 * Position of the first page break that is not at the document's top level, or null.
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @param {import('@tiptap/pm/model').NodeType} type - Page break type.
 * @param {Set<number>|null} wanted - Top-level child indexes to look in (null = all).
 * @returns {number|null} Position.
 */
const findNestedBreak = (doc, type, wanted) => {
  let found = null;
  doc.forEach((child, offset, index) => {
    if (found !== null || (wanted && !wanted.has(index)) || child.isLeaf) return;
    child.descendants((node, pos) => {
      if (found !== null) return false;
      if (node.type !== type) return true;
      found = offset + 1 + pos;
      return false;
    });
  });
  return found;
};

/**
 * Build a transaction that moves nested page breaks to the top level. A break inside a list,
 * table, quote or other container goes just after its top-level ancestor; a break directly
 * inside a wrapper (generated field, trust section) splits the wrapper there, so it stays at
 * the same place in the text. A moved break keeps its attributes and inherits the review state
 * of the suggestion it was part of.
 * @param {import('@tiptap/pm/state').EditorState} state - State to normalize.
 * @param {number[]|null} indexes - Top-level child indexes to check (null = all).
 * @returns {import('@tiptap/pm/state').Transaction|null} Transaction, or null when nothing is nested.
 */
export function buildNestedPageBreakFix(state, indexes = null) {
  const type = state.schema.nodes[PAGE_BREAK_NODE];
  if (!type) return null;
  if (findNestedBreak(state.doc, type, indexes ? new Set(indexes) : null) === null) return null;

  const tr = state.tr;
  // One break at a time: a fix can split or move blocks, so the next one is looked up again.
  for (let guard = 0; guard < 1000; guard += 1) {
    const pos = findNestedBreak(tr.doc, type, null);
    if (pos === null) break;
    const $pos = tr.doc.resolve(pos);
    const node = tr.doc.nodeAt(pos);
    const attrs = movedBreakAttrs($pos, node);
    const wrapperDepth = wrapperDepthAt($pos);
    let at = pos;
    if ($pos.depth > wrapperDepth) {
      // Nested in a list, table, quote, ...: just after that block (still inside any wrappers).
      const target = $pos.after(wrapperDepth + 1);
      tr.delete(pos, pos + node.nodeSize);
      at = tr.mapping.maps[tr.mapping.maps.length - 1].map(target, 1);
      tr.insert(at, type.create(attrs));
    } else if (attrs !== node.attrs) {
      tr.setNodeMarkup(pos, undefined, attrs);
    }
    if (tr.doc.resolve(liftPageBreak(tr, at)).depth) break; // could not reach the top level; stop
  }
  return tr.docChanged ? tr : null;
}

/**
 * Mirror the editor-only attributes (review state from DiffMarks, block id from BlockId) onto
 * the node view, so an Inky review highlights an inserted or deleted break, the review bar can
 * scroll to it and "scroll to block" finds it. Saved HTML (renderHTML) stays the exact
 * canonical markup; these attributes only live in the editor DOM.
 * @param {HTMLElement} dom - Node view element.
 * @param {import('@tiptap/pm/model').Node} node - Page break node.
 * @returns {void}
 */
const syncEditorDomAttributes = (dom, node) => {
  const diffBlock = node.attrs.diffBlock === 'insert' || node.attrs.diffBlock === 'delete' ? node.attrs.diffBlock : null;
  [
    ['data-diff-block', diffBlock],
    ['data-change-id', diffBlock && node.attrs.diffChangeId ? String(node.attrs.diffChangeId) : null],
    ['data-block-id', node.attrs.blockId ? String(node.attrs.blockId) : null],
  ].forEach(([name, value]) => {
    if (value) {
      if (dom.getAttribute(name) !== value) dom.setAttribute(name, value);
    } else if (dom.hasAttribute(name)) {
      dom.removeAttribute(name);
    }
  });
};

/** Keyboard shortcut, kept above HardBreak (priority 100) so Mod-Enter reaches it first. */
const PageBreakKeymap = Extension.create({
  name: 'pageBreakKeymap',
  priority: 200,

  addKeyboardShortcuts() {
    return {
      'Mod-Enter': ({ editor }) => {
        if (!editor.isEditable || selectionInCode(editor.state)) return false;
        return editor.commands.insertPageBreak();
      },
    };
  },
});

/**
 * Ends the break's undo step after `insertPageBreak()`, so typing that follows right away is a
 * separate step. It runs as the very last appendTransaction (lowest priority), after the other
 * plugins have added their follow-ups for the insert (BlockId stamping the new paragraph's id,
 * for one): those stay in the break's step. Closing any earlier would turn each of them into an
 * undo step of its own that changes nothing visible.
 */
const PageBreakHistory = Extension.create({
  name: 'pageBreakHistory',
  priority: 1,

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('quillPageBreakHistory'),
        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some((tr) => tr.getMeta(pageBreakPluginKey)?.inserted)) return null;
          // An empty transaction carrying closeHistory resets the group without adding a step.
          return closeHistory(newState.tr).setMeta('addToHistory', false);
        },
      }),
    ];
  },
});

const PageBreak = Node.create({
  name: PAGE_BREAK_NODE,
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addExtensions() {
    return [PageBreakKeymap, PageBreakHistory];
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-page-break]',
        priority: 60,
        getAttrs: (element) => (element.getAttribute('data-page-break') === 'false' ? false : null),
      },
      { tag: 'div', priority: 60, getAttrs: (element) => (isLegacyBreakElement(element) ? null : false) },
      { tag: 'p', priority: 60, getAttrs: (element) => (isLegacyBreakElement(element) ? null : false) },
      // Pasted from Google Docs: `<hr style="page-break-before:always;display:none">` (above
      // HorizontalRule). Word's `<br style="page-break-before:always">` is rewritten before
      // parsing instead (normalizePastedPageBreaks), because a leaf parsed from a `br` is never
      // allowed to close the paragraph around it.
      { tag: 'hr', priority: 60, getAttrs: (element) => (styleHasPageBreak(element.getAttribute('style')) ? null : false) },
    ];
  },

  renderHTML() {
    return ['div', { 'data-page-break': 'true', class: 'page-break' }];
  },

  renderText() {
    return '\n';
  },

  addNodeView() {
    return ({ node: initialNode }) => {
      const dom = document.createElement('div');
      dom.setAttribute('data-page-break', 'true');
      dom.className = 'page-break';
      dom.setAttribute('role', 'separator');
      dom.setAttribute('aria-label', PAGE_BREAK_LABEL);
      dom.setAttribute('contenteditable', 'false');
      const label = document.createElement('span');
      label.className = 'page-break-label';
      label.setAttribute('aria-hidden', 'true');
      label.textContent = PAGE_BREAK_LABEL;
      dom.appendChild(label);
      syncEditorDomAttributes(dom, initialNode);
      return {
        dom,
        ignoreMutation: () => true,
        update: (node) => {
          if (node.type !== initialNode.type) return false;
          syncEditorDomAttributes(dom, node);
          return true;
        },
      };
    };
  },

  addCommands() {
    return {
      insertPageBreak: () => ({ tr, dispatch, editor }) => {
        const type = this.type;
        // Check before touching `tr`: TipTap dispatches it even when a command returns false.
        if (!tr.doc.canReplaceWith(0, 0, type)) return false;
        if (!dispatch) return true;
        // A typed-over range or Select all is replaced by the break, like in Docs and Word.
        const { selection } = tr;
        if (!selection.empty && (selection instanceof TextSelection || selection instanceof AllSelection)) {
          tr.deleteSelection();
        }
        const placement = resolvePageBreakPlacement(tr.selection);
        let insertAt = placement.insertAt;
        if (placement.splitAt !== null) {
          const $split = tr.doc.resolve(placement.splitAt);
          const parent = $split.parent;
          const attrs = getSplittedAttributes(editor.extensionManager.attributes, parent.type.name, parent.attrs);
          tr.split(placement.splitAt, 1, [{ type: parent.type, attrs }]);
          insertAt = placement.splitAt + 1;
        }

        tr.insert(insertAt, type.create());
        // Inside a generated field or trust section: split the wrapper so the break is top level.
        const breakAt = placement.wrapperDepth ? liftPageBreak(tr, insertAt) : insertAt;
        const after = breakAt + 1;
        const next = tr.doc.resolve(after).nodeAfter;
        // The caret goes to the start of the content after the break. When that is not text
        // (an image, AI field, diagram, divider, another break) a new empty line starts the next
        // page instead, so nothing is left node-selected for the next keystroke to replace.
        let caret = null;
        if (next && next.isTextblock) {
          caret = TextSelection.create(tr.doc, after + 1);
        } else if (next && !next.isAtom && !next.isLeaf) {
          const found = Selection.findFrom(tr.doc.resolve(after), 1, true);
          if (found && found.from < after + next.nodeSize) caret = found;
        }
        if (!caret) {
          const paragraph = editor.schema.nodes.paragraph || tr.doc.type.contentMatch.defaultType;
          tr.insert(after, paragraph.create());
          caret = TextSelection.create(tr.doc, after + 1);
        }
        tr.setSelection(caret);
        tr.setMeta(pageBreakPluginKey, { inserted: true });
        // Its own undo step: not merged with the typing before it (PageBreakHistory ends it after).
        closeHistory(tr);
        tr.scrollIntoView();
        return true;
      },
    };
  },

  onCreate() {
    const tr = buildNestedPageBreakFix(this.editor.state);
    if (tr) {
      tr.setMeta('addToHistory', false);
      tr.setMeta('preventUpdate', true);
      this.editor.view.dispatch(tr);
    }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pageBreakPluginKey,
        props: {
          transformPastedHTML: (html) => normalizePastedPageBreaks(html),
        },
        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some((tr) => tr.docChanged)) return null;
          // insertPageBreak never nests a break; its undo step is ended by PageBreakHistory.
          if (transactions.some((tr) => tr.getMeta(pageBreakPluginKey)?.inserted)) return null;
          return buildNestedPageBreakFix(newState, touchedTopLevelIndexes(transactions, newState.doc));
        },
      }),
    ];
  },
});

export default PageBreak;
